import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FilterToolbar = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters, 
  totalResults,
  onExport 
}) => {
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'open', label: 'Open' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' }
  ];

  const severityOptions = [
    { value: '', label: 'All Severities' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const assigneeOptions = [
    { value: '', label: 'All Assignees' },
    { value: 'john-doe', label: 'John Doe' },
    { value: 'sarah-chen', label: 'Sarah Chen' },
    { value: 'mike-johnson', label: 'Mike Johnson' },
    { value: 'emily-davis', label: 'Emily Davis' },
    { value: 'unassigned', label: 'Unassigned' }
  ];

  const systemOptions = [
    { value: '', label: 'All Systems' },
    { value: 'web-server', label: 'Web Server' },
    { value: 'database', label: 'Database' },
    { value: 'network', label: 'Network Infrastructure' },
    { value: 'email', label: 'Email System' },
    { value: 'firewall', label: 'Firewall' },
    { value: 'endpoint', label: 'Endpoint Security' }
  ];

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleDateRangeChange = (type, value) => {
    onFiltersChange({
      ...filters,
      dateRange: {
        ...filters?.dateRange,
        [type]: value
      }
    });
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.search) count++;
    if (filters?.status) count++;
    if (filters?.severity) count++;
    if (filters?.assignee) count++;
    if (filters?.impactedSystem) count++;
    if (filters?.dateRange?.start || filters?.dateRange?.end) count++;
    return count;
  };

  const exportOptions = [
    { value: 'csv', label: 'Export as CSV' },
    { value: 'pdf', label: 'Export as PDF' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      {/* Main Filter Row */}
      <div className="flex flex-col lg:flex-row gap-4 mb-4">
        {/* Search Input */}
        <div className="flex-1 min-w-0">
          <Input
            type="search"
            placeholder="Search incidents by ID, title, or description..."
            value={filters?.search}
            onChange={(e) => handleFilterChange('search', e?.target?.value)}
            className="w-full"
          />
        </div>

        {/* Quick Filters */}
        <div className="flex flex-wrap gap-2">
          <Select
            options={statusOptions}
            value={filters?.status}
            onChange={(value) => handleFilterChange('status', value)}
            placeholder="Status"
            className="w-32"
          />
          
          <Select
            options={severityOptions}
            value={filters?.severity}
            onChange={(value) => handleFilterChange('severity', value)}
            placeholder="Severity"
            className="w-32"
          />

          <Button
            variant="outline"
            onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            iconName={isAdvancedOpen ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            Advanced
            {getActiveFilterCount() > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-primary text-primary-foreground rounded-full">
                {getActiveFilterCount()}
              </span>
            )}
          </Button>
        </div>
      </div>
      {/* Advanced Filters */}
      {isAdvancedOpen && (
        <div className="border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <Select
              label="Assigned To"
              options={assigneeOptions}
              value={filters?.assignee}
              onChange={(value) => handleFilterChange('assignee', value)}
              placeholder="Select assignee"
            />

            <Select
              label="Impacted System"
              options={systemOptions}
              value={filters?.impactedSystem}
              onChange={(value) => handleFilterChange('impactedSystem', value)}
              placeholder="Select system"
            />

            <Input
              label="Date From"
              type="date"
              value={filters?.dateRange?.start || ''}
              onChange={(e) => handleDateRangeChange('start', e?.target?.value)}
            />

            <Input
              label="Date To"
              type="date"
              value={filters?.dateRange?.end || ''}
              onChange={(e) => handleDateRangeChange('end', e?.target?.value)}
            />
          </div>
        </div>
      )}
      {/* Results Summary and Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-4">
          <span className="text-sm text-muted-foreground">
            {totalResults?.toLocaleString()} incidents found
          </span>
          
          {getActiveFilterCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              iconName="X"
              iconPosition="left"
            >
              Clear Filters
            </Button>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <Select
            options={exportOptions}
            value=""
            onChange={(value) => onExport(value)}
            placeholder="Export"
            className="w-32"
          />
          
          <Button
            variant="outline"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => window.location?.reload()}
          >
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterToolbar;